/**
 * Client Profile API
 *
 * API functions for fetching and updating client profiles
 */

import { buildApiUrl, handleApiResponse } from '@/src/shared/api';
import { fetchWithTokenRefresh } from '@/src/shared/api/refreshToken';

/**
 * Client Profile API response type
 * Matches backend ClientsService.getProfile return type
 * Note: Named ClientProfileResponse to avoid conflict with UI ClientProfile type
 */
export interface ClientProfileResponse {
  id: string;
  userId: string | null;
  // Actor fields
  name: string;
  displayName: string;
  phone: string | null;
  avatarUrl: string | null;
  // Client profile fields
  bio: string | null;
  notificationRadius: number;
  showPhone: boolean;
  showEmail: boolean;
  showLocation: boolean;
  allowMessages: boolean;
  budgetMin: number | null;
  budgetMax: number | null;
  // Location fields
  latitude: number | null;
  longitude: number | null;
  address: string | null;
  city: string | null;
  province: string | null;
  postalCode: string | null;
  // Statistics
  totalOrders: number;
  completedOrders: number;
  canceledOrders: number;
  // User data
  user: {
    id: string;
    email: string;
    isVerified: boolean;
    createdAt: string;
  } | null;
  // Metadata
  createdAt: string;
  updatedAt: string;
}

/**
 * DTO for updating client profile
 */
export interface UpdateClientProfileDto {
  bio?: string;
  notificationRadius?: number;
  showPhone?: boolean;
  showEmail?: boolean;
  showLocation?: boolean;
  allowMessages?: boolean;
  budgetMin?: number;
  budgetMax?: number;
}

/**
 * DTO for updating client location
 */
export interface UpdateClientLocationDto {
  latitude: number;
  longitude: number;
  address?: string;
  city?: string;
  province?: string;
  postalCode?: string;
  serviceRadius?: number;
}

/**
 * Fetch current user's client profile
 * GET /clients/me
 */
export async function fetchMyClientProfile(): Promise<ClientProfileResponse> {
  const url = buildApiUrl('/clients/me');

  const response = await fetchWithTokenRefresh(url, {
    method: 'GET',
    credentials: 'include',
  });

  return handleApiResponse<ClientProfileResponse>(response);
}

/**
 * Update client profile (bio, preferences, privacy settings)
 * PATCH /clients/me
 */
export async function updateClientProfile(
  dto: UpdateClientProfileDto
): Promise<ClientProfileResponse> {
  const url = buildApiUrl('/clients/me');

  // Filter out undefined values
  const filteredDto = Object.fromEntries(
    Object.entries(dto).filter(([, value]) => value !== undefined)
  );

  const response = await fetchWithTokenRefresh(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(filteredDto),
  });

  return handleApiResponse<ClientProfileResponse>(response);
}

/**
 * Update client location
 * PATCH /clients/me/location
 */
export async function updateClientLocation(
  dto: UpdateClientLocationDto
): Promise<ClientProfileResponse> {
  const url = buildApiUrl('/clients/me/location');

  const response = await fetchWithTokenRefresh(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(dto),
  });

  return handleApiResponse<ClientProfileResponse>(response);
}
